import * as THREE from 'three';
import type { WaterBlock } from '@vrlearning/lesson-schema';
import { buildSurf, shoreRise, type Shore } from '../surf.js';
import { buildWater, type Water } from '../water.js';
import type { WorldBuilder } from '../world.js';
import type { StatefulBlock } from './features.js';
import type { PartRegistry } from './parts.js';

export type BlockEnvironment = {
  /** The world's land: the sea's bed, where the surf climbs to. */
  readonly ground: (x: number, z: number) => number;
  readonly towardsSun: THREE.Vector3;
  /** The sky's horizon colour, which the water reflects. */
  readonly horizon: string;
  /** Its meshes' names start with this (`beach-sea`, `beach-surf`). */
  readonly name: string;
};

/**
 * The water block (W2; from water.ts and surf.ts): a disc of water at `level` over the world's ground,
 * its surf along `surf.shore`; `level` is stateful. `levels` is the lowest and highest level any
 * feature sets, so the surf knows how far up the ground it climbs.
 */
export function buildWaterBlock(
  world: WorldBuilder,
  block: WaterBlock,
  env: BlockEnvironment,
  levels: readonly [number, number] = [block.level, block.level],
  /** The package's named parts: a block with `mesh` takes its shape from them as they arrive. */
  parts?: PartRegistry,
): StatefulBlock {
  let water: Water | undefined;
  let wanted: number | undefined;
  let tide: (level: number) => void = () => {};
  const make = (shape?: THREE.BufferGeometry): void => {
    water = buildWater({
      level: block.level,
      bed: env.ground,
      radius: shape ? 0 : block.radius,
      deep: block.deep,
      shallow: block.shallow,
      waves: block.waves,
      foam: block.foam,
      sky: new THREE.Color(env.horizon).lerp(new THREE.Color(0x7d93a8), 0.4),
      sunDirection: env.towardsSun,
      sunColour: 0xfff1dc,
      ...(shape ? { shape } : {}),
    });
    water.mesh.name = `${env.name}-sea`;
    world.addDressing(water.mesh);
    const built = water;
    world.onFrame((_dt, elapsed) => built.update(elapsed));
    if (wanted !== undefined) water.setLevel(wanted);
  };
  if (block.mesh && parts) {
    parts.when([block.mesh], (meshes) => {
      if (water) return;
      make(flatSheet(meshes, block.level));
      for (const mesh of meshes) mesh.visible = false;
    });
  } else make();

  if (block.surf) {
    const shore: Shore = { points: block.surf.shore, closed: block.surf.closed };
    const [low, high] = levels;
    const rise = high > low ? shoreRise([shore], env.ground, low, high) : undefined;
    const surf = buildSurf([shore], block.level, rise);
    surf.mesh.name = `${env.name}-surf`;
    world.addDressing(surf.mesh);
    world.onFrame((_dt, elapsed) => surf.update(elapsed));
    tide = (level) => surf.setTide(high > low ? (level - low) / (high - low) : 0);
  }
  return {
    set(setting, value) {
      if (setting !== 'level') return;
      wanted = value as number;
      water?.setLevel(wanted);
      tide(wanted);
    },
  };
}

/** The named meshes as one flat sheet in world space, at `level`: just positions and indices. */
function flatSheet(meshes: readonly THREE.Mesh[], level: number): THREE.BufferGeometry {
  const positions: number[] = [];
  const index: number[] = [];
  const at = new THREE.Vector3();
  for (const mesh of meshes) {
    mesh.updateWorldMatrix(true, false);
    const source = mesh.geometry.getAttribute('position');
    const base = positions.length / 3;
    for (let i = 0; i < source.count; i++) {
      at.fromBufferAttribute(source, i).applyMatrix4(mesh.matrixWorld);
      positions.push(at.x, level, at.z);
    }
    const order = mesh.geometry.index;
    if (order) for (let i = 0; i < order.count; i++) index.push(base + order.getX(i));
    else for (let i = 0; i < source.count; i++) index.push(base + i);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(index);
  geometry.computeBoundingSphere();
  return geometry;
}
